import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Image as ImageIcon, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const PublicGalleries = () => {
  const [galleries, setGalleries] = useState([]);
  const [photographer, setPhotographer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdminGalleries, setIsAdminGalleries] = useState(false);
  const [searchParams] = useSearchParams();
  const photographerId = searchParams.get("photographer");

  const fetchGalleries = useCallback(async () => {
    try {
      if (photographerId) {
        // Fetch photographer-specific galleries
        const url = `${API}/galleries/public/list?photographer_id=${photographerId}`;
        const response = await axios.get(url);
        setGalleries(response.data);
        setIsAdminGalleries(false);
        
        // Get photographer info
        if (response.data.length > 0) {
          const photog = response.data[0]?.photographer;
          if (photog) {
            setPhotographer(photog);
          }
        }
      } else {
        // Fetch admin featured galleries for demo/homepage
        const response = await axios.get(`${API}/admin/galleries/public/featured`);
        setGalleries(response.data);
        setIsAdminGalleries(true);
      }
    } catch (error) {
      toast.error("Failed to load galleries");
    } finally {
      setLoading(false);
    }
  }, [photographerId]);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  // Branding
  const brandName = photographer?.company_name || "360Lumina";
  const logoUrl = photographer?.logo_url ? `${API.replace("/api", "")}${photographer.logo_url}` : null;
  
  // Generate back link to photographer's landing page
  const getBackLink = () => {
    if (!photographerId || !photographer) return "/";
    
    // Use company_name or user name to build the URL
    const businessName = photographer.company_name || photographer.user?.name;
    if (businessName) {
      const urlSafeName = businessName
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
      return `/photographer/${urlSafeName}`;
    }
    return "/";
  };
  
  const backLink = getBackLink();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" data-testid="public-galleries">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12">
        <Link to={backLink} className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          {photographerId && photographer ? `Back to ${brandName}` : "Back to Home"}
        </Link>
        
        <Link to={backLink} className="flex items-center gap-3 mb-6 hover:opacity-80 transition-opacity">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-8 max-w-[160px] object-contain" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-primary" />
              <span className="text-2xl font-serif">{brandName}</span>
            </>
          )}
        </Link>
        
        <h1 className="text-4xl md:text-5xl tracking-tight mb-4">
          {isAdminGalleries ? "Demo Galleries" : "Event Galleries"}
        </h1>
        <p className="text-muted-foreground text-lg">
          {isAdminGalleries 
            ? "Explore sample galleries showcasing what 360Lumina can do for your photography business"
            : "Browse and purchase photos from recent events"
          }
        </p>
      </div>

      <div className="max-w-7xl mx-auto">
        {galleries.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-lg mb-2">No galleries available yet</p>
              <p className="text-muted-foreground">
                {isAdminGalleries 
                  ? "Check back soon for demo galleries"
                  : "Check back soon for new event photos"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleries.map((gallery, index) => (
              <motion.div
                key={gallery.gallery_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={isAdminGalleries ? `/admin-gallery/${gallery.gallery_id}` : `/galleries/${gallery.gallery_id}`}>
                  <Card className="bg-card border-border card-hover overflow-hidden group">
                    <div className="aspect-[4/3] relative bg-muted">
                      {gallery.cover_image_filename || gallery.cover_photo ? (
                        <img
                          src={isAdminGalleries 
                            ? `${API}/admin/galleries/public/${gallery.gallery_id}/media/${gallery.cover_photo}`
                            : `${API}/media/${gallery.gallery_id}/${gallery.cover_image_filename}`
                          }
                          alt={gallery.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-4 text-center bg-gradient-to-br from-muted to-muted/70">
                          <p className="text-2xl font-serif text-muted-foreground mb-2 line-clamp-2">
                            {gallery.title}
                          </p>
                          {gallery.event_date && (
                            <p className="text-lg text-muted-foreground/70">
                              {new Date(gallery.event_date).toLocaleDateString('en-US', { 
                                month: 'long', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-sm uppercase tracking-widest">View Gallery</span>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-serif text-xl mb-2">{gallery.title}</h3>
                      {gallery.category && (
                        <p className="text-sm text-primary mb-2">{gallery.category}</p>
                      )}
                      <p className="text-sm text-muted-foreground mb-2">
                        {gallery.event_date || gallery.description?.substring(0, 50) || "Featured Gallery"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {gallery.asset_count || gallery.photos?.length || 0} photos available
                      </p>
                      {(gallery.photographer?.company_name || gallery.photographer?.user?.name) && (
                        <p className="text-sm text-primary mt-2">
                          by {gallery.photographer?.company_name || gallery.photographer?.user?.name}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicGalleries;
